import { useState, useEffect, useMemo } from 'react';
import {
  Download,
  Search,
  FileCheck,
  Activity,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  Save,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  Package,
  PlayCircle,
  CheckCircle2,
  Globe,
  Plus,
  Plug
} from 'lucide-react';
import apiClient from '../services/api';
import axios from 'axios';
import { getActiveProfile, getConnectorConfig, saveConnectorConfig, saveConnectorConfigs } from '../utils/connectorConfig';
import useStore from '../store/useStore';
import { formatDateTime } from '../utils/dateFormat';

export default function ConsumerDashboard() {
  const { setConnectorFormOpen, connectorKey } = useStore();
  const activeProfile = useMemo(() => getActiveProfile(), [connectorKey]);

  const [stats, setStats] = useState({
    availableAssets: 0,
    activeContracts: 0,
    completedTransfers: 0,
    pendingNegotiations: 0
  });
  const [negotiations, setNegotiations] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [contractsWithTermStatus, setContractsWithTermStatus] = useState([]);
  const [detailedTransfers, setDetailedTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [activeSection, setActiveSection] = useState(null); // 'contracts' | 'transfers' | 'catalog' | null

  // Catalog browsing state
  const [providerDspUrl, setProviderDspUrl] = useState(
    () => activeProfile?.providerDspUrl || getConnectorConfig('providerDspUrl') || ''
  );
  const [providerManagementUrl, setProviderManagementUrl] = useState(
    () => activeProfile?.providerEdcUrl || getConnectorConfig('providerManagementUrl') || ''
  );
  // Tracks which registered connector button was actually clicked, so the
  // "selected" highlight can't light up two buttons whose URLs happen to
  // match (e.g. one saved with the wrong value) - matching by value instead
  // of identity was the bug behind that.
  const [selectedProviderConnectorId, setSelectedProviderConnectorId] = useState(null);
  const consumerDspUrl = activeProfile?.consumerDspUrl || 'https://enact-dataspace.iti.gr/edc-consumer/api/dsp';
  const [catalogOffers, setCatalogOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [negotiationInProgress, setNegotiationInProgress] = useState(false);
  const [negotiationResult, setNegotiationResult] = useState(null);
  const [negotiationStatus, setNegotiationStatus] = useState(null); // { state, contractAgreementId }
  const [statusChecking, setStatusChecking] = useState(false);
  const [registeredConnectors, setRegisteredConnectors] = useState([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [transferStarting, setTransferStarting] = useState(false);
  const [destinationUrl, setDestinationUrl] = useState(
    getConnectorConfig('transferDestinationUrl')
  );
  const [contractFilter, setContractFilter] = useState('active'); // 'active' | 'all' | 'terminated'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [apiStatus, setApiStatus] = useState({
    backend: 'checking',
    edc: 'checking'
  });
  const [loading, setLoading] = useState(true);
  const consumerUrl = activeProfile?.consumerEdcUrl || '';
  const apiKey = activeProfile?.consumerApiKey || '';

  useEffect(() => {
    // Test backend API connectivity on mount
    testAPIConnectivity();

    if (consumerUrl) {
      fetchConsumerStatistics();
    } else {
      setLoading(false);
    }
  }, [consumerUrl]);

  // Reset to page 1 when section changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSection]);

  // Fetch registered connectors when catalog section opens
  useEffect(() => {
    if (activeSection === 'catalog') {
      fetchRegisteredConnectors();
    }
  }, [activeSection]);

  const fetchConsumerStatistics = async () => {
    if (!consumerUrl) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setApiStatus(prev => ({ ...prev, edc: 'checking' }));

      // Check if the consumerUrl is external (non-HTTPS or different domain)
      const isExternalConnector = consumerUrl.startsWith('http://') ||
                                   !consumerUrl.startsWith('https://enact-dataspace.iti.gr');

      const requestBody = {
        '@context': {},
        'limit': 1000,
        'offset': 0
      };

      let negotiationsRes, transfersRes;

      if (isExternalConnector) {
        // Use proxy for external/HTTP connectors to avoid CORS and Mixed Content issues
        const headers = {
          'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('kc_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Fetch data from EDC Management API using POST requests through proxy
        [negotiationsRes, transfersRes] = await Promise.all([
          axios.post('/api/v1/edc-proxy', {
            targetUrl: consumerUrl,
            path: '/api/management/v2/contractnegotiations/request',
            method: 'POST',
            data: requestBody,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers }).catch(() => ({ data: [] })),
          axios.post('/api/v1/edc-proxy', {
            targetUrl: consumerUrl,
            path: '/api/management/v2/transferprocesses/request',
            method: 'POST',
            data: requestBody,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers }).catch(() => ({ data: [] })),
        ]);
      } else {
        // Direct request for local HTTPS connectors
        const headers = {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-Api-Key': apiKey } : {})
        };

        // Fetch data from EDC Management API using POST requests
        [negotiationsRes, transfersRes] = await Promise.all([
          axios.post(`${consumerUrl}/api/management/v2/contractnegotiations/request`, requestBody, { headers }).catch(() => ({ data: [] })),
          axios.post(`${consumerUrl}/api/management/v2/transferprocesses/request`, requestBody, { headers }).catch(() => ({ data: [] })),
        ]);
      }

      const negotiationsData = Array.isArray(negotiationsRes.data) ? negotiationsRes.data : [];
      const transfersData = Array.isArray(transfersRes.data) ? transfersRes.data : [];

      console.log('=== Consumer Dashboard Debug ===');
      console.log('Total negotiations:', negotiationsData.length);
      console.log('Total transfers:', transfersData.length);

      setNegotiations(negotiationsData);
      setTransfers(transfersData);

      // Get enhanced statistics, contracts, and transfer history from backend API
      try {
        // Fetch stats, contracts, and transfer history in parallel
        const [statsRes, contractsRes, transfersRes] = await Promise.all([
          apiClient.post('/edc/consumer/contract-stats', { consumerUrl, apiKey }),
          apiClient.post('/edc/consumer/contracts', { consumerUrl, apiKey }),
          apiClient.post('/edc/consumer/transfer-history', { consumerUrl, apiKey })
        ]);

        console.log('=== Enhanced Data from Backend ===');
        console.log('Stats:', statsRes.data);
        console.log('Contracts:', contractsRes.data);
        console.log('Transfers:', transfersRes.data);

        const pendingFromData = negotiationsData.filter(n => {
          const state = n.state || n['edc:state'];
          return ['REQUESTING', 'REQUESTED', 'OFFERING', 'OFFERED', 'ACCEPTING', 'ACCEPTED'].includes(state);
        }).length;

        const completedFromData = transfersData.filter(t => {
          const state = t.state || t['edc:state'];
          return state === 'COMPLETED';
        }).length;

        setStats({
          availableAssets: 0,
          activeContracts: statsRes.data.activeContracts,
          completedTransfers: completedFromData || statsRes.data.completedTransfers || 0,
          pendingNegotiations: pendingFromData,
        });

        // Store contracts with termination status for proper filtering
        setContractsWithTermStatus(contractsRes.data.contracts || []);

        // Store detailed transfer history
        setDetailedTransfers(transfersRes.data.transfers || []);
      } catch (statsError) {
        console.error('Failed to get enhanced stats, falling back to basic calculation:', statsError);

        // Fallback to basic calculation if backend endpoint fails
        const completedTransfers = transfersData.filter(t => {
          const state = t.state || t['edc:state'];
          return state === 'COMPLETED';
        }).length;

        const pendingNegotiations = negotiationsData.filter(n => {
          const state = n.state || n['edc:state'];
          return ['REQUESTING', 'REQUESTED', 'OFFERING', 'OFFERED', 'ACCEPTING', 'ACCEPTED'].includes(state);
        }).length;

        const contractsWithAgreement = negotiationsData.filter(n => {
          const agreementId = n.contractAgreementId || n['edc:contractAgreementId'];
          return agreementId && agreementId !== null && agreementId !== '';
        });

        const activeContracts = contractsWithAgreement.filter(n => {
          const state = n.state || n['edc:state'];
          return state === 'FINALIZED';
        }).length;

        setStats({
          availableAssets: 0,
          activeContracts,
          completedTransfers,
          pendingNegotiations,
        });

        // Clear contracts with term status on error
        setContractsWithTermStatus([]);
      }

      setApiStatus(prev => ({ ...prev, edc: 'connected' }));
    } catch (error) {
      console.error('Failed to fetch consumer statistics:', error);
      setApiStatus(prev => ({ ...prev, edc: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const testAPIConnectivity = async () => {
    try {
      await apiClient.get('/data-exchange/status');
      setApiStatus(prev => ({ ...prev, backend: 'connected' }));
    } catch (error) {
      setApiStatus(prev => ({ ...prev, backend: 'error' }));
    }
  };

  const refreshData = () => {
    fetchConsumerStatistics();
  };

  const fetchRegisteredConnectors = async () => {
    setConnectorsLoading(true);
    try {
      const token = localStorage.getItem('kc_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get('/api/connectors', { headers });
      setRegisteredConnectors(response.data.connectors || []);
    } catch (error) {
      console.error('Failed to fetch registered connectors:', error);
    } finally {
      setConnectorsLoading(false);
    }
  };

  // Browse catalog from provider
  const handleBrowseCatalog = async () => {
    if (!consumerUrl || !providerDspUrl) {
      setCatalogError('Consumer URL and Provider DSP URL are required');
      return;
    }

    setCatalogLoading(true);
    setCatalogError(null);
    setSelectedOffer(null);
    setNegotiationResult(null);

    try {
      // Pass all required parameters matching ConsumerDataExchange page
      const response = await apiClient.get('/data-exchange/consumer/catalog', {
        params: {
          providerUrl: providerManagementUrl,
          consumerUrl: consumerUrl,
          providerDspUrl: providerDspUrl,
          consumerDspUrl: consumerDspUrl,
          apiKey: apiKey || 'ApiKeyDefaultValue'
        }
      });

      const offers = response.data.availableOffers || [];
      setCatalogOffers(offers);

      if (offers.length === 0) {
        setCatalogError('No offers found in the catalog');
      }
    } catch (error) {
      console.error('Failed to browse catalog:', error);
      setCatalogError(error.response?.data?.message || error.message || 'Failed to fetch catalog');
      setCatalogOffers([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Negotiate contract for selected offer
  const handleNegotiateContract = async () => {
    if (!selectedOffer) {
      setCatalogError('Please select an offer first');
      return;
    }

    setNegotiationInProgress(true);
    setCatalogError(null);

    try {
      const response = await apiClient.post('/data-exchange/consumer/negotiate', {
        providerUrl: providerManagementUrl,
        consumerUrl: consumerUrl,
        providerDspUrl: providerDspUrl,
        consumerDspUrl: consumerDspUrl,
        assetId: selectedOffer.assetId,
        offerId: selectedOffer.offerId,
        policy: selectedOffer.policy,
        providerId: selectedOffer.providerId || selectedOffer.providerParticipantId || 'connector-provider',
        apiKey: apiKey || 'ApiKeyDefaultValue'
      });

      const negotiationId = response.data.negotiationId || response.data.response?.['@id'];
      setNegotiationResult({
        success: true,
        negotiationId: negotiationId,
        message: 'Contract negotiation started successfully!'
      });

      // Refresh stats after negotiation
      setTimeout(() => {
        fetchConsumerStatistics();
      }, 2000);

      // Auto-check status after a few seconds
      setTimeout(() => handleCheckNegotiationStatus(negotiationId), 3000);
    } catch (error) {
      console.error('Failed to negotiate contract:', error);
      setNegotiationResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to negotiate contract'
      });
    } finally {
      setNegotiationInProgress(false);
    }
  };

  // Check negotiation status
  const handleCheckNegotiationStatus = async (negId) => {
    const id = negId || negotiationResult?.negotiationId;
    if (!id) return;

    setStatusChecking(true);
    try {
      const response = await apiClient.get(`/data-exchange/consumer/negotiation/${id}`, {
        params: {
          providerUrl: providerManagementUrl,
          consumerUrl: consumerUrl,
          providerDspUrl: providerDspUrl,
          consumerDspUrl: consumerDspUrl,
          apiKey: apiKey || 'ApiKeyDefaultValue'
        }
      });

      const state = response.data.state || response.data.status || 'UNKNOWN';
      const agreementId = response.data.contractAgreementId;

      setNegotiationStatus({ state, contractAgreementId: agreementId });

      if (agreementId) {
        setNegotiationResult(prev => ({
          ...prev,
          contractAgreementId: agreementId,
          state: state
        }));
      } else {
        setNegotiationResult(prev => ({
          ...prev,
          state: state
        }));
        // Auto-retry if still in progress
        if (['REQUESTED', 'AGREED', 'VERIFIED', 'ACCEPTING', 'REQUESTING'].includes(state)) {
          setTimeout(() => handleCheckNegotiationStatus(id), 3000);
        }
      }
    } catch (error) {
      console.error('Failed to check negotiation status:', error);
    } finally {
      setStatusChecking(false);
    }
  };

  // Start transfer from a successful negotiation
  const handleStartTransferFromNegotiation = async () => {
    const agreementId = negotiationResult?.contractAgreementId;
    if (!agreementId) return;

    setTransferStarting(true);
    try {
      const response = await apiClient.post('/data-exchange/consumer/transfer', {
        providerUrl: providerManagementUrl,
        consumerUrl: consumerUrl,
        providerDspUrl: providerDspUrl,
        consumerDspUrl: consumerDspUrl,
        contractId: agreementId,
        assetId: selectedOffer?.assetId,
        destination: {
          '@type': 'DataAddress',
          type: 'HttpData',
          baseUrl: destinationUrl || 'https://enact-dataspace.iti.gr/api/v1/data-exchange/data-receiver',
          method: 'POST',
          contentType: 'application/json'
        },
        apiKey: apiKey || 'ApiKeyDefaultValue'
      });

      const transferId = response.data.transferId || response.data.response?.['@id'];
      setNegotiationResult(prev => ({
        ...prev,
        transferId: transferId,
        message: 'Data transfer initiated successfully!'
      }));

      // Refresh stats
      setTimeout(() => fetchConsumerStatistics(), 2000);
    } catch (error) {
      console.error('Failed to start transfer:', error);
      setNegotiationResult(prev => ({
        ...prev,
        transferError: error.response?.data?.message || error.message || 'Failed to start transfer'
      }));
    } finally {
      setTransferStarting(false);
    }
  };

  // Start transfer for a contract
  const handleStartTransfer = async (contractAgreementId) => {
    if (!contractAgreementId) return;

    setNegotiationInProgress(true);
    try {
      const response = await apiClient.post('/data-exchange/consumer/transfer', {
        providerUrl: providerManagementUrl,
        consumerUrl: consumerUrl,
        providerDspUrl: providerDspUrl,
        consumerDspUrl: consumerDspUrl,
        contractAgreementId: contractAgreementId,
        apiKey: apiKey || 'ApiKeyDefaultValue'
      });

      setNegotiationResult({
        success: true,
        transferId: response.data.transferId,
        message: 'Data transfer initiated successfully!'
      });

      // Refresh stats
      setTimeout(() => {
        fetchConsumerStatistics();
      }, 2000);
    } catch (error) {
      console.error('Failed to start transfer:', error);
      setNegotiationResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to start transfer'
      });
    } finally {
      setNegotiationInProgress(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'error':
        return <AlertTriangle className="text-red-400" size={20} />;
      default:
        return <Clock className="text-yellow-400 animate-pulse" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'error':
        return 'Connection Error';
      default:
        return 'Checking...';
    }
  };

  // Pagination logic
  const getPaginatedItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  // Get contracts with agreement IDs (fallback from negotiations)
  const contractsFromNegotiations = negotiations.filter(n => {
    const agreementId = n.contractAgreementId || n['edc:contractAgreementId'];
    return agreementId && agreementId !== null && agreementId !== '';
  });

  // Use contracts with termination status if available, otherwise fall back to negotiations
  const hasTermStatusData = contractsWithTermStatus.length > 0;

  // Filter contracts based on selected filter using terminationStatus
  const getFilteredContracts = () => {
    if (hasTermStatusData) {
      // Use contracts with proper termination status from wrapper API
      switch (contractFilter) {
        case 'active':
          return contractsWithTermStatus.filter(c => c.terminationStatus === 'ONGOING');
        case 'terminated':
          return contractsWithTermStatus.filter(c => c.terminationStatus === 'TERMINATED');
        case 'all':
        default:
          return contractsWithTermStatus;
      }
    } else {
      // Fallback to negotiation state-based filtering
      switch (contractFilter) {
        case 'active':
          return contractsFromNegotiations.filter(n => {
            const state = n.state || n['edc:state'];
            return state === 'FINALIZED';
          });
        case 'terminated':
          return contractsFromNegotiations.filter(n => {
            const state = n.state || n['edc:state'];
            return state === 'TERMINATED';
          });
        case 'all':
        default:
          return contractsFromNegotiations;
      }
    }
  };

  const filteredContracts = getFilteredContracts();

  const pendingNegotiationsList = negotiations.filter(n => {
    const state = n.state || n['edc:state'];
    return ['REQUESTING', 'REQUESTED', 'OFFERING', 'OFFERED', 'ACCEPTING', 'ACCEPTED'].includes(state);
  });

  // Calculate counts using termination status when available
  const activeContractsCount = hasTermStatusData
    ? contractsWithTermStatus.filter(c => c.terminationStatus === 'ONGOING').length
    : contractsFromNegotiations.filter(n => (n.state || n['edc:state']) === 'FINALIZED').length;

  const terminatedContractsCount = hasTermStatusData
    ? contractsWithTermStatus.filter(c => c.terminationStatus === 'TERMINATED').length
    : contractsFromNegotiations.filter(n => (n.state || n['edc:state']) === 'TERMINATED').length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-2">
            Consumer Dashboard
          </h1>
          <p className="text-gray-400">
            Discover and access data assets
          </p>
        </div>
        {!consumerUrl && (
          <button
            onClick={() => setConnectorFormOpen(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Plus size={16} />
            Set up connector
          </button>
        )}
      </div>

      {/* Connected banner */}
      {consumerUrl && (
        <div className="card bg-green-900/10 border-green-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-400 flex-shrink-0" size={22} />
            <div>
              <h3 className="font-semibold text-green-400">{activeProfile?.name} — connected</h3>
              <p className="text-sm text-gray-400">Your EDC connector is set up and active</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refreshData} disabled={loading}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={() => setConnectorFormOpen(true)}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <Plus size={15} />
              Add another connector
            </button>
          </div>
        </div>
      )}

      {/* First-time setup invitation */}
      {!consumerUrl && (
        <div className="card bg-blue-900/10 border-blue-800 text-center py-10">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/20 border border-blue-700 flex items-center justify-center mx-auto mb-4">
            <Plug className="text-blue-400" size={28} />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Connect your consumer connector</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Add a name and your Consumer EDC management API URL to get started.
          </p>
          <button
            onClick={() => setConnectorFormOpen(true)}
            className="btn btn-primary inline-flex items-center gap-2 mx-auto"
          >
            <Plus size={16} />
            Set up connector
          </button>
        </div>
      )}

      {/* API Status */}
      {consumerUrl && (
        <div className="grid grid-cols-1 gap-6">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Consumer EDC Connector</p>
                <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus.edc)}</p>
              </div>
              {getStatusIcon(apiStatus.edc)}
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {consumerUrl && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <button
            onClick={() => setActiveSection(activeSection === 'contracts' ? null : 'contracts')}
            className="card hover:border-green-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <FileCheck className="text-green-400" size={24} />
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.activeContracts}
            </p>
            <p className="text-gray-400 text-sm">Active Contracts</p>
          </button>

          <button
            onClick={() => setActiveSection(activeSection === 'transfers' ? null : 'transfers')}
            className="card hover:border-purple-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <Download className="text-purple-400" size={24} />
              <CheckCircle className="text-green-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.completedTransfers}
            </p>
            <p className="text-gray-400 text-sm">Completed Transfers</p>
          </button>

          <button
            onClick={() => setActiveSection(activeSection === 'negotiations' ? null : 'negotiations')}
            className="card hover:border-yellow-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <Activity className="text-yellow-400" size={24} />
              <Clock className="text-yellow-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.pendingNegotiations}
            </p>
            <p className="text-gray-400 text-sm">Pending Negotiations</p>
          </button>

          <button
            onClick={() => setActiveSection(activeSection === 'catalog' ? null : 'catalog')}
            className="card hover:border-blue-500/50 transition-all cursor-pointer text-left bg-gradient-to-br from-blue-900/20 to-transparent"
          >
            <div className="flex items-center justify-between mb-4">
              <Package className="text-blue-400" size={24} />
              <Search className="text-blue-400" size={20} />
            </div>
            <p className="text-xl font-bold text-white mb-2">
              Browse Catalog
            </p>
            <p className="text-gray-400 text-sm">Find & request data</p>
          </button>
        </div>
      )}

      {/* Pending Negotiations Section */}
      {consumerUrl && activeSection === 'negotiations' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Activity className="text-yellow-400" size={24} />
              Pending Negotiations ({pendingNegotiationsList.length})
            </h2>
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {pendingNegotiationsList.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Activity className="mx-auto mb-3 opacity-50" size={48} />
              <p>No pending negotiations found</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {getPaginatedItems(pendingNegotiationsList).map((neg) => {
                  const negId = neg['@id'] || neg.contractNegotiationId || '';
                  const state = neg.state || neg['edc:state'] || 'UNKNOWN';
                  const counterPartyId = neg.counterPartyId || neg['edc:counterPartyId'] || null;
                  const counterPartyAddress = neg.counterPartyAddress || neg['edc:counterPartyAddress'] || null;
                  const createdAt = neg.createdAt || neg['edc:createdAt'];
                  const createdDate = createdAt ? new Date(createdAt) : null;

                  return (
                    <div key={negId} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-yellow-500/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-white text-sm truncate flex-1 mr-2">
                          {negId.split(':').pop() || negId.substring(0, 20)}
                        </h3>
                        <span className="text-xs px-2 py-1 rounded bg-yellow-900/30 text-yellow-400 whitespace-nowrap">
                          {state}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400 space-y-1">
                        <p className="font-mono text-xs truncate" title={negId}>
                          ID: {negId.length > 40 ? negId.substring(0, 40) + '...' : negId}
                        </p>
                        {counterPartyId && (
                          <p className="text-xs truncate" title={counterPartyId}>
                            Provider: <span className="text-blue-400">{counterPartyId}</span>
                          </p>
                        )}
                        {counterPartyAddress && (
                          <p className="text-xs text-gray-500 truncate" title={counterPartyAddress}>
                            DSP: {counterPartyAddress}
                          </p>
                        )}
                        {createdDate && (
                          <p className="text-xs text-gray-500">
                            Started: {formatDateTime(createdDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {getTotalPages(pendingNegotiationsList) > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400">
                    Page {currentPage} of {getTotalPages(pendingNegotiationsList)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(getTotalPages(pendingNegotiationsList), p + 1))}
                      disabled={currentPage === getTotalPages(pendingNegotiationsList)}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Browse Catalog Section */}
      {consumerUrl && activeSection === 'catalog' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Package className="text-blue-400" size={24} />
              Browse Provider Catalog
            </h2>
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Provider URL Configuration */}
          <div className="bg-gray-800/50 rounded-lg p-4 mb-4">

            {/* Registered connector quick-pick */}
            <div className="mb-4 pb-4 border-b border-gray-700">
              <p className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <Globe size={14} />
                Select a registered provider connector
              </p>
              {connectorsLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Loader2 size={14} className="animate-spin" />
                  Loading connectors...
                </div>
              ) : registeredConnectors.filter(c => c.connector_type === 'provider').length === 0 ? (
                <p className="text-sm text-gray-500 italic">No registered provider connectors found — enter URLs manually below.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {registeredConnectors
                    .filter(c => c.connector_type === 'provider')
                    .map(connector => {
                      const isSelected = selectedProviderConnectorId === connector.connector_id;
                      const healthColor =
                        connector.health_status === 'healthy' ? 'bg-green-400' :
                        connector.health_status === 'unhealthy' ? 'bg-red-400' :
                        'bg-gray-500';
                      return (
                        <button
                          key={connector.connector_id}
                          onClick={() => {
                            setSelectedProviderConnectorId(connector.connector_id);
                            setProviderDspUrl(connector.dsp_url);
                            saveConnectorConfig('providerDspUrl', connector.dsp_url);
                            setProviderManagementUrl(connector.management_url);
                            saveConnectorConfig('providerManagementUrl', connector.management_url);
                          }}
                          title={`Management: ${connector.management_url}\nDSP: ${connector.dsp_url}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                            isSelected
                              ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                              : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-blue-500/50 hover:text-white'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${healthColor}`} />
                          {connector.connector_name}
                        </button>
                      );
                    })
                  }
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Globe size={16} className="inline mr-2" />
                Provider DSP Endpoint
              </label>
              <input
                type="text"
                value={providerDspUrl}
                onChange={(e) => {
                  const dsp = e.target.value;
                  setProviderDspUrl(dsp);
                  saveConnectorConfig('providerDspUrl', dsp);
                  // Auto-derive management URL from DSP URL
                  const mgmt = dsp.replace(/\/api\/dsp$/, '').replace(/\/dsp$/, '');
                  setProviderManagementUrl(mgmt);
                  saveConnectorConfig('providerManagementUrl', mgmt);
                }}
                
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleBrowseCatalog}
              disabled={catalogLoading}
              className="btn btn-primary"
            >
              {catalogLoading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Search size={16} />
              )}
              {catalogLoading ? 'Loading...' : 'Fetch Catalog'}
            </button>
          </div>

          {/* Error Display */}
          {catalogError && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
              <div className="flex gap-3">
                <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                <p className="text-red-400">{catalogError}</p>
              </div>
            </div>
          )}

          {/* Negotiation Result & Status */}
          {negotiationResult && (
            <div className="border rounded-lg p-4 mb-4 space-y-3 bg-gray-800/50 border-gray-700">
              {/* Status header */}
              <div className="flex gap-3">
                {negotiationResult.success ? (
                  <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                ) : (
                  <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                )}
                <div className="flex-1">
                  <p className={negotiationResult.success ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>
                    {negotiationResult.transferId
                      ? 'Data transfer initiated!'
                      : negotiationResult.contractAgreementId
                      ? 'Contract agreement reached!'
                      : negotiationResult.message}
                  </p>
                  {negotiationResult.negotiationId && (
                    <p className="text-xs text-gray-400 mt-1 font-mono break-all">
                      Negotiation ID: {negotiationResult.negotiationId}
                    </p>
                  )}
                  {negotiationResult.state && (
                    <p className="text-xs text-gray-400 mt-1">
                      State: <span className={`font-semibold ${
                        negotiationResult.state === 'FINALIZED' ? 'text-green-400' :
                        negotiationResult.state === 'TERMINATED' ? 'text-red-400' :
                        'text-yellow-400'
                      }`}>{negotiationResult.state}</span>
                    </p>
                  )}
                  {negotiationResult.contractAgreementId && (
                    <p className="text-xs text-green-400 mt-1 font-mono break-all">
                      Agreement ID: {negotiationResult.contractAgreementId}
                    </p>
                  )}
                  {negotiationResult.transferId && (
                    <p className="text-xs text-blue-400 mt-1 font-mono break-all">
                      Transfer ID: {negotiationResult.transferId}
                    </p>
                  )}
                  {negotiationResult.transferError && (
                    <p className="text-xs text-red-400 mt-1">
                      Transfer error: {negotiationResult.transferError}
                    </p>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-2 border-t border-gray-700">
                {/* Check Status button - show when negotiation started but no agreement yet */}
                {negotiationResult.negotiationId && !negotiationResult.contractAgreementId && !negotiationResult.transferId && (
                  <button
                    onClick={() => handleCheckNegotiationStatus()}
                    disabled={statusChecking}
                    className="btn btn-secondary text-sm"
                  >
                    {statusChecking ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    {statusChecking ? 'Checking...' : 'Check Status'}
                  </button>
                )}

                {/* Destination URL + Start Transfer - show when agreement reached */}
                {negotiationResult.contractAgreementId && !negotiationResult.transferId && (
                  <div className="flex flex-col gap-2 flex-1">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        <Globe size={12} className="inline mr-1" />
                        Destination URL
                      </label>
                      <input
                        type="text"
                        value={destinationUrl}
                        onChange={(e) => {
                          setDestinationUrl(e.target.value);
                          saveConnectorConfig('transferDestinationUrl', e.target.value);
                        }}
                        
                        className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleStartTransferFromNegotiation}
                      disabled={transferStarting || !destinationUrl}
                      className="btn btn-primary text-sm self-start"
                    >
                      {transferStarting ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <Download size={14} />
                      )}
                      {transferStarting ? 'Starting...' : 'Start Data Transfer'}
                    </button>
                  </div>
                )}

                {/* Reset button */}
                <button
                  onClick={() => {
                    setNegotiationResult(null);
                    setNegotiationStatus(null);
                    setSelectedOffer(null);
                  }}
                  className="btn btn-secondary text-sm ml-auto"
                >
                  <X size={14} />
                  Reset
                </button>
              </div>
            </div>
          )}

          {/* Available Offers */}
          {catalogOffers.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-300">
                  Available Assets ({catalogOffers.length})
                </h4>
                {selectedOffer && (
                  <button
                    onClick={handleNegotiateContract}
                    disabled={negotiationInProgress}
                    className="btn btn-primary"
                  >
                    {negotiationInProgress ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <PlayCircle size={16} />
                    )}
                    Request Selected Asset
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
                {catalogOffers.map((offer, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedOffer(offer)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all overflow-hidden ${
                      selectedOffer?.assetId === offer.assetId
                        ? 'border-blue-500 bg-blue-900/20'
                        : 'border-gray-700 bg-gray-800/50 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2 mb-2 min-w-0">
                      <h5 className="font-semibold text-white truncate flex-1 min-w-0" title={offer.title || offer.assetId}>{offer.title || offer.assetId}</h5>
                      {selectedOffer?.assetId === offer.assetId && (
                        <CheckCircle2 className="text-blue-400 flex-shrink-0" size={20} />
                      )}
                    </div>
                    {offer.description && (
                      <p className="text-sm text-gray-400 mb-2 line-clamp-2">{offer.description}</p>
                    )}
                    <p className="text-xs text-gray-500 font-mono truncate" title={offer.assetId}>
                      Asset: {offer.assetId}
                    </p>
                    {offer.offerId && (
                      <p className="text-xs text-gray-600 font-mono truncate mt-1" title={offer.offerId}>
                        Offer: {offer.offerId.substring(0, 30)}...
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !catalogLoading && !catalogError && (
              <div className="text-center py-8 text-gray-400">
                <Package className="mx-auto mb-3 opacity-50" size={48} />
                <p>Click "Fetch Catalog" to browse available assets</p>
              </div>
            )
          )}
        </div>
      )}

      {/* Contracts Section - Paginated with Filter */}
      {consumerUrl && activeSection === 'contracts' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileCheck className="text-green-400" size={24} />
              Contracts ({filteredContracts.length})
            </h2>
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => {
                setContractFilter('active');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                contractFilter === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Active ({activeContractsCount})
            </button>
            <button
              onClick={() => {
                setContractFilter('all');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                contractFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              All ({hasTermStatusData ? contractsWithTermStatus.length : contractsFromNegotiations.length})
            </button>
            <button
              onClick={() => {
                setContractFilter('terminated');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                contractFilter === 'terminated'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Terminated ({terminatedContractsCount})
            </button>
          </div>

          {filteredContracts.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileCheck className="mx-auto mb-3 opacity-50" size={48} />
              <p>No {contractFilter} contracts found</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {getPaginatedItems(filteredContracts).map((contract) => {
                  // Handle both wrapper API format and negotiation format
                  const contractId = contract.contractNegotiationId || contract['@id'] || '';
                  const agreementId = contract.contractAgreementId || contract['edc:contractAgreementId'] || 'N/A';
                  const assetName = contract.assetTitle || contract.assetId || '';
                  const termStatus = contract.terminationStatus;
                  const negState = contract.state || contract['edc:state'];

                  // Determine display status - prefer terminationStatus if available
                  const isTerminated = termStatus === 'TERMINATED' || negState === 'TERMINATED';
                  const isActive = termStatus === 'ONGOING' || negState === 'FINALIZED' || negState === 'VERIFIED';
                  const statusLabel = termStatus || negState || 'UNKNOWN';

                  return (
                    <div key={contractId} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-green-500/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-white text-sm truncate flex-1">
                          {contractId.split(':').pop() || contractId.substring(0, 20)}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          isTerminated
                            ? 'bg-red-900/30 text-red-400'
                            : isActive
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-yellow-900/30 text-yellow-400'
                        }`}>
                          {statusLabel}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400 space-y-1">
                        <p className="truncate font-mono text-xs">ID: {agreementId.substring(0, 40)}...</p>
                        {assetName && <p className="text-xs text-blue-400">Asset: {assetName}</p>}
                        <p className="text-xs text-gray-500">Type: {contract.direction || 'CONSUMER'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {getTotalPages(filteredContracts) > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400">
                    Page {currentPage} of {getTotalPages(filteredContracts)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(getTotalPages(filteredContracts), p + 1))}
                      disabled={currentPage === getTotalPages(filteredContracts)}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Transfer History Section - Table View */}
      {consumerUrl && activeSection === 'transfers' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Download className="text-purple-400" size={24} />
              Transfer History ({detailedTransfers.length > 0 ? detailedTransfers.length : transfers.length})
            </h2>
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {(detailedTransfers.length === 0 && transfers.length === 0) ? (
            <div className="text-center py-8 text-gray-400">
              <Download className="mx-auto mb-3 opacity-50" size={48} />
              <p>No transfer processes found</p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-left text-gray-400">
                      <th className="pb-3 pr-4">Direction</th>
                      <th className="pb-3 pr-4">Last Updated</th>
                      <th className="pb-3 pr-4">Asset</th>
                      <th className="pb-3 pr-4">State</th>
                      <th className="pb-3 pr-4">Counterparty ID</th>
                      <th className="pb-3 pr-4">Connector Endpoint</th>
                      <th className="pb-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems(detailedTransfers.length > 0 ? detailedTransfers : transfers).map((transfer) => {
                      const transferId = transfer.transferProcessId || transfer['@id'] || '';
                      const assetName = transfer.assetName || transfer.assetId || 'N/A';
                      const state = transfer.state || 'UNKNOWN';
                      const direction = transfer.direction || 'CONSUMING';
                      const lastUpdated = transfer.lastUpdatedDate ? new Date(transfer.lastUpdatedDate) : null;
                      const counterPartyId = transfer.counterPartyParticipantId || 'N/A';
                      const connectorEndpoint = transfer.counterPartyConnectorEndpoint || 'N/A';

                      // Calculate relative time
                      const getRelativeTime = (date) => {
                        if (!date) return 'N/A';
                        const now = new Date();
                        const diffMs = now - date;
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMinutes = Math.floor(diffMs / (1000 * 60));

                        if (diffDays > 30) return `about ${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
                        if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                        if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                        if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
                        return 'just now';
                      };

                      return (
                        <tr key={transferId} className="border-b border-gray-800 hover:bg-gray-800/30">
                          <td className="py-3 pr-4">
                            <Download size={16} className={direction === 'CONSUMING' ? 'text-blue-400' : 'text-green-400'} />
                          </td>
                          <td className="py-3 pr-4 text-gray-300">{getRelativeTime(lastUpdated)}</td>
                          <td className="py-3 pr-4 text-white font-medium">{assetName}</td>
                          <td className="py-3 pr-4">
                            <span className={`text-xs px-2 py-1 rounded ${
                              state === 'COMPLETED'
                                ? 'bg-green-900/30 text-green-400'
                                : state === 'STARTED' || state === 'REQUESTING'
                                ? 'bg-blue-900/30 text-blue-400'
                                : state === 'TERMINATED'
                                ? 'bg-red-900/30 text-red-400'
                                : 'bg-gray-900/30 text-gray-400'
                            }`}>
                              {state}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-gray-400 text-xs max-w-[200px] truncate" title={counterPartyId}>
                            {counterPartyId}
                          </td>
                          <td className="py-3 pr-4 text-gray-400 text-xs max-w-[200px] truncate" title={connectorEndpoint}>
                            {connectorEndpoint}
                          </td>
                          <td className="py-3">
                            <button
                              onClick={() => {
                                const s = transfer.state;
                                setSelectedTransfer({
                                  transferProcessId: transfer.transferProcessId || transfer['@id'] || '',
                                  assetId: transfer.assetId || transfer['edc:assetId'] || '',
                                  assetName: transfer.assetName || transfer.assetId || transfer['edc:assetId'] || '',
                                  contractAgreementId: transfer.contractAgreementId || transfer['edc:contractId'] || transfer['edc:contractAgreementId'] || '',
                                  counterPartyConnectorEndpoint: transfer.counterPartyConnectorEndpoint || transfer['edc:counterPartyAddress'] || '',
                                  counterPartyParticipantId: transfer.counterPartyParticipantId || transfer['edc:counterPartyId'] || '',
                                  createdDate: transfer.createdDate || (transfer['edc:createdAt'] ? new Date(transfer['edc:createdAt']).toISOString() : ''),
                                  direction: transfer.direction || (transfer['edc:type'] === 'PROVIDER' ? 'PROVIDING' : 'CONSUMING'),
                                  lastUpdatedDate: transfer.lastUpdatedDate || (transfer['edc:stateTimestamp'] ? new Date(transfer['edc:stateTimestamp']).toISOString() : ''),
                                  stateCode: (typeof s === 'object' ? s?.code : '') ?? '',
                                  state: (typeof s === 'object' ? s?.name : s) || '',
                                  simplifiedState: (typeof s === 'object' ? s?.simplifiedState : '') || '',
                                });
                              }}
                              className="text-blue-400 hover:text-blue-300 text-xs"
                            >
                              Show Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {getTotalPages(detailedTransfers.length > 0 ? detailedTransfers : transfers) > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-800 mt-4">
                  <p className="text-sm text-gray-400">
                    Page {currentPage} of {getTotalPages(detailedTransfers.length > 0 ? detailedTransfers : transfers)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(getTotalPages(detailedTransfers.length > 0 ? detailedTransfers : transfers), p + 1))}
                      disabled={currentPage === getTotalPages(detailedTransfers.length > 0 ? detailedTransfers : transfers)}
                      className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Transfer Details Modal */}
      {selectedTransfer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedTransfer(null)}>
          <div className="bg-gray-900 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileCheck size={24} className="text-purple-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedTransfer.assetName || selectedTransfer.assetId}</h3>
                  <p className="text-sm text-gray-400">Transfer History Details</p>
                </div>
              </div>
              <button onClick={() => setSelectedTransfer(null)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex">
                <span className="text-purple-400 w-48">assetId:</span>
                <span className="text-gray-300">"{selectedTransfer.assetId}"</span>
              </div>
              <div className="flex">
                <span className="text-purple-400 w-48">assetName:</span>
                <span className="text-gray-300">"{selectedTransfer.assetName}"</span>
              </div>
              <div className="flex flex-col">
                <span className="text-purple-400">contractAgreementId:</span>
                <span className="text-gray-300 text-xs break-all ml-4">"{selectedTransfer.contractAgreementId}"</span>
              </div>
              <div className="flex flex-col">
                <span className="text-purple-400">counterPartyConnectorEndpoint:</span>
                <span className="text-gray-300 text-xs break-all ml-4">"{selectedTransfer.counterPartyConnectorEndpoint}"</span>
              </div>
              <div className="flex flex-col">
                <span className="text-purple-400">counterPartyParticipantId:</span>
                <span className="text-gray-300 text-xs break-all ml-4">"{selectedTransfer.counterPartyParticipantId}"</span>
              </div>
              <div className="flex">
                <span className="text-purple-400 w-48">createdDate:</span>
                <span className="text-gray-300">"{formatDateTime(selectedTransfer.createdDate)}"</span>
              </div>
              <div className="flex">
                <span className="text-purple-400 w-48">direction:</span>
                <span className="text-gray-300">"{selectedTransfer.direction}"</span>
              </div>
              <div className="flex">
                <span className="text-purple-400 w-48">lastUpdatedDate:</span>
                <span className="text-gray-300">"{formatDateTime(selectedTransfer.lastUpdatedDate)}"</span>
              </div>
              <div className="flex flex-col">
                <span className="text-purple-400">state:</span>
                <div className="ml-4 text-gray-300">
                  <div>code: {selectedTransfer.stateCode}</div>
                  <div>name: "{selectedTransfer.state}"</div>
                  <div>simplifiedState: "{selectedTransfer.simplifiedState}"</div>
                </div>
              </div>
              <div className="flex">
                <span className="text-purple-400 w-48">transferProcessId:</span>
                <span className="text-gray-300 text-xs">"{selectedTransfer.transferProcessId}"</span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={() => setSelectedTransfer(null)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
